import React, { useState, useRef, useEffect } from "react";
import { useApp } from "../lib/AppContext";
import { supabase } from "../lib/supabase";
import { compressImage } from "../lib/photoUpload";
import { validateImageFile } from "../lib/fileValidation";
import { PHOTO_UPLOAD_URL } from "../lib/apiConfig";

export default function ReceiptScanner({ onBack, preselectedJobId }) {
  const { currentUser, orgId, jobs } = useApp();
  const fileInputRef = useRef(null);

  const [scannedImage, setScannedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(preselectedJobId || "");
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Auto-open camera on mount
  useEffect(() => {
    // Small delay to ensure component is mounted
    const timer = setTimeout(() => {
      if (!scannedImage) {
        fileInputRef.current?.click();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear input so same file can be selected again
    e.target.value = "";

    // Validate file type and size
    const validation = await validateImageFile(file, {
      maxSize: 15 * 1024 * 1024,
    });
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setScannedImage(previewUrl);
    setImageFile(file);
    setError(null);
  };

  // Rescan - open camera again
  const handleRescan = () => {
    if (scannedImage) {
      URL.revokeObjectURL(scannedImage);
    }
    setScannedImage(null);
    setImageFile(null);
    setError(null);
    fileInputRef.current?.click();
  };

  // Submit receipt
  const handleSubmit = async () => {
    if (!imageFile || !selectedJobId) {
      setError("Please select a job");
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // 1. Compress image
      const compressed = await compressImage(imageFile, 1920, 0.85);

      // 2. Upload to R2 via Cloudflare Worker
      const timestamp = Date.now();
      const key = `receipts/${orgId}/${timestamp}.jpg`;

      const uploadResponse = await fetch(
        `${PHOTO_UPLOAD_URL}?key=${encodeURIComponent(key)}`,
        {
          method: "PUT",
          body: compressed,
          headers: { "Content-Type": "image/jpeg" },
        },
      );

      if (!uploadResponse.ok) {
        const errText = await uploadResponse.text().catch(() => "");
        throw new Error(`Upload failed: ${errText || uploadResponse.status}`);
      }

      const result = await uploadResponse.json();
      const storagePath = result.key;

      // 3. Create receipt record
      const { error: insertError } = await supabase
        .from("t4s_receipts")
        .insert({
          org_id: orgId,
          uploaded_by: currentUser?.id,
          storage_path: storagePath,
          job_id: selectedJobId,
          field_note: note || null,
          status: "pending",
          source: "field",
          created_at: new Date().toISOString(),
        });

      if (insertError) throw insertError;

      // 5. Success!
      setSuccess(true);

      // Clean up and go back after brief delay
      setTimeout(() => {
        if (scannedImage) {
          URL.revokeObjectURL(scannedImage);
        }
        onBack();
      }, 1500);
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to upload receipt. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    if (scannedImage) {
      URL.revokeObjectURL(scannedImage);
    }
    onBack();
  };

  // Success state
  if (success) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            padding: "24px",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "#10b981",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "16px",
            }}
          >
            <svg
              width="40"
              height="40"
              fill="none"
              stroke="white"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h2 style={{ margin: 0, color: "#1e293b", fontSize: "20px" }}>
            Receipt Submitted!
          </h2>
          <p style={{ color: "#64748b", marginTop: "8px" }}>
            Sent to office for processing
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <button
          onClick={handleCancel}
          style={{
            background: "transparent",
            border: "none",
            color: "white",
            fontSize: "16px",
            cursor: "pointer",
            padding: "8px",
            marginLeft: "-8px",
          }}
        >
          Cancel
        </button>
        <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
          Snap Receipt
        </h1>
        <div style={{ width: "60px" }} />
      </div>

      {/* Content */}
      <div style={{ padding: "16px" }}>
        {/* Image Preview or Capture Button */}
        {scannedImage ? (
          <div
            style={{
              position: "relative",
              marginBottom: "20px",
              borderRadius: "12px",
              overflow: "hidden",
              background: "#f1f5f9",
              border: "2px solid #e2e8f0",
            }}
          >
            <img
              src={scannedImage}
              alt="Receipt preview"
              style={{
                width: "100%",
                maxHeight: "300px",
                objectFit: "contain",
                display: "block",
              }}
            />
            <button
              onClick={handleRescan}
              style={{
                position: "absolute",
                bottom: "12px",
                right: "12px",
                background: "white",
                border: "none",
                borderRadius: "20px",
                padding: "10px 16px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#3b82f6",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span>📷</span> Retake
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              color: "white",
              borderRadius: "16px",
              padding: "40px 24px",
              border: "none",
              cursor: "pointer",
              marginBottom: "20px",
              boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "12px" }}>🧾</div>
            <div style={{ fontSize: "18px", fontWeight: "600" }}>
              Take Photo of Receipt
            </div>
            <div style={{ fontSize: "14px", opacity: 0.8, marginTop: "8px" }}>
              Use your camera to capture the receipt
            </div>
          </button>
        )}

        {/* Job Selector */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Job <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <select
            value={selectedJobId}
            onChange={(e) => setSelectedJobId(e.target.value)}
            disabled={uploading}
            style={{
              width: "100%",
              padding: "14px 12px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              fontSize: "16px",
              color: "#1f2937",
              appearance: "none",
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 12px center",
              backgroundSize: "20px",
            }}
          >
            <option value="">Select a job...</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.job_number ? `${job.job_number} - ` : ""}
                {job.name}
              </option>
            ))}
          </select>
        </div>

        {/* Note Field */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              marginBottom: "8px",
            }}
          >
            Note (optional)
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={uploading}
            placeholder="e.g., Lunch for crew, HD supplies"
            maxLength={100}
            style={{
              width: "100%",
              padding: "14px 12px",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              background: "#f9fafb",
              fontSize: "16px",
              color: "#1f2937",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Error Message */}
        {error && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "10px",
              padding: "12px 16px",
              marginBottom: "16px",
              color: "#dc2626",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!scannedImage || !selectedJobId || uploading}
          style={{
            width: "100%",
            padding: "16px",
            borderRadius: "12px",
            border: "none",
            background:
              !scannedImage || !selectedJobId || uploading
                ? "#9ca3af"
                : "#10b981",
            color: "white",
            fontSize: "16px",
            fontWeight: "600",
            cursor:
              !scannedImage || !selectedJobId || uploading
                ? "not-allowed"
                : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {uploading ? (
            <>
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTopColor: "white",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Uploading...
            </>
          ) : (
            <>
              <span>✓</span> Submit Receipt
            </>
          )}
        </button>
      </div>

      {/* Info Card */}
      <div style={{ padding: "0 16px 24px" }}>
        <div
          style={{
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: "12px",
            padding: "16px",
            display: "flex",
            gap: "12px",
          }}
        >
          <div style={{ color: "#3b82f6", fontSize: "18px" }}>💡</div>
          <div style={{ fontSize: "14px", color: "#1e40af" }}>
            <strong>Quick capture.</strong> Snap the receipt, pick the job, and
            submit. The office will handle the rest!
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
